import { TextFieldTypes } from '@ionic/core';
import { IonFab, IonFabButton, IonIcon, IonInput, IonItem, IonSpinner } from '@ionic/react';
import { refreshOutline } from 'ionicons/icons';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { GetVatInfo, useVatInfo } from '../../../api/hooks/customers/useVatInfo';
import { TooltipInInput } from '../../../components/molecules/TooltipInInput';
import { showError } from '../../../config/utils/formDiffUtils';
import { disableSubmitOnEnter } from '../../../config/utils/formUtils';

interface Props {
  field: any;
  formik: any;
  onVatInfoRetrieved: (data: GetVatInfo) => void; // new optional callback prop
}

export const CustomerVatNumber = ({ field, formik, onVatInfoRetrieved }: Props) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const hasError = useMemo(
    () => showError(formik, formik?.errors?.vatNumber),
    [formik?.errors?.vatNumber, formik?.touched],
  );
  const customerTypeSelected = useMemo(() => formik?.values?.customerTypeId, [formik?.values?.customerTypeId]);

  const { refetch, isLoading, isRefetching } = useVatInfo(
    formik?.values?.vatNumber || '',
    customerTypeSelected?.aadeCode || customerTypeSelected?.object?.aadeCode,
    id,
  );

  const isRefetchVatInfoDisabled = useMemo(() => {
    return !formik?.values?.vatNumber || isLoading || isRefetching;
  }, [formik?.values?.vatNumber, isLoading, isRefetching]);

  const refetchVatInfo = useCallback(() => {
    if (!isLoading && !isRefetching) {
      refetch().then((result) => {
        const response = result?.data;

        if (response?.success) {
          onVatInfoRetrieved?.(response);
        }
      });
    }
  }, [isLoading, isRefetching, onVatInfoRetrieved, refetch]);

  const getLoading = useCallback(() => {
    if (isLoading || isRefetching) {
      return (
        <div style={{ position: 'absolute', top: -2, right: 2 }}>
          <IonSpinner name="lines" style={{ width: 24, height: 24 }}></IonSpinner>
        </div>
      );
    }
  }, [isLoading, isRefetching]);

  const getTooltip = useCallback(() => {
    if (field.tooltip) {
      return <TooltipInInput value={field.tooltip} />;
    }
  }, [field.tooltip]);

  const getRefreshCustomerDetails = useCallback(() => {
    return (
      <IonFab slot="fixed" horizontal="center" vertical="center">
        <IonFabButton
          onClick={refetchVatInfo}
          color="transparent"
          className="add-fab"
          disabled={isRefetchVatInfoDisabled}>
          <IonIcon icon={refreshOutline} size="medium" color="primary" />
        </IonFabButton>
      </IonFab>
    );
  }, [isRefetchVatInfoDisabled, refetchVatInfo]);

  return (
    <div className="positionRelative">
      {getLoading()}
      <div className="padding-b-16 padding-l-12 flex-start-center-row-wrap">
        <div>{t(`FORMS.${field.label}`)} *</div>
        <div className="flex-center-center-row-wrap margin-l-12 positionRelative">{getRefreshCustomerDetails()}</div>
      </div>
      <div key={field?.name} className="flex-center-start-column-nowrap positionRelative width-300px">
        {getTooltip()}
        <IonItem
          id={field.name}
          className={`width-300px height-50px border-radius-input g-select-border cursorPointer ${
            hasError && 'b-field-border-error'
          }`}
          mode="md">
          <IonInput
            className="height-48px"
            mode="md"
            type={field.type as TextFieldTypes}
            aria-label="delivery"
            name={field.name}
            onKeyPress={disableSubmitOnEnter}
            placeholder={t(`FORMS.${field.placeholder}`) + '...'}
            // See `CustomerSimpleInput.tsx` - `ionInput` (not `ionChange`) keeps formik's value in
            // sync with every keystroke, so a re-render before blur can't revert what was typed.
            onIonInput={formik.handleChange}
            value={formik.values?.[field.name]}
            disabled={field?.disabled}
          />
        </IonItem>
      </div>
    </div>
  );
};
